import {
  Flex,
  Text,
  useColorModeValue,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useToast,
  Switch,
  Stack,
  Box,
  Select,
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { columnsDataPdfTemplate } from 'variables/columnsData';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { totalRow } from 'state/roles-permissions';
import { AddIcon } from '@chakra-ui/icons';
import { BsSearch } from 'react-icons/bs';
import { RxReset } from 'react-icons/rx';
import { usePdfTemplate } from 'hooks/usePdfTemplate';
import { checkForbidden } from 'helpers/checkForbidden';
import { listPdfTemplateAtom } from 'state/show';
import SearchTable2 from 'components/Tables/SearchTable2';
import { useHistory } from 'react-router-dom';

// status: 0 = All, 1 = Active, 2 = InActive
const STATUS_OPTIONS = [
  { id: '', name: 'All' },
  { id: '1', name: 'Active' },
  { id: '2', name: 'InActive' },
];


function DataTablePdfTemplate() {
  const textColor = useColorModeValue('gray.700', 'white');
  const listPdfTemplate = useRecoilValue(listPdfTemplateAtom);
  const totalRows = useRecoilValue(totalRow);
  const pdfTemplateActions = usePdfTemplate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCreate, setIsCreate] = useState(true);
  const [currentTemplateID, setCurrentTemplateID] = useState();
  const [isFetching, setIsFetching] = useState(false);
  const [newListTemplates, setNewListTemplates] = useState([]);
  const [refreshPagingDefault, setRefreshPagingDefault] = useState(false);
  const history = useHistory();
  const toast = useToast();

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('This field is required'),
    code: Yup.string().required('This field is required'),
    data_structure: Yup.string()
      .test('is-valid-json', 'Invalid JSON format', (value) => {
        if (!value) return true;
        try { JSON.parse(value); return true; } catch { return false; }
      }),
  });
  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: { name: '', code: '', data_structure: '', isActive: true },
  };
  const { register, handleSubmit, formState, reset, setValue } = useForm(formOptions);
  const {
    register: registerSearch,
    handleSubmit: handleSubmitSearch,
    watch: watchSearch,
    reset: resetSearch,
  } = useForm();
  const { errors, isSubmitting } = formState;

  const nameSearch = watchSearch('nameSearch');
  const statusSearch = watchSearch('statusSearch');

  useEffect(() => {
    if (listPdfTemplate) {
      const newList = listPdfTemplate.map((i) => ({
        ...i,
        filters: { name: nameSearch, status: statusSearch },
      }));
      setNewListTemplates(newList);
    }
  }, [listPdfTemplate]);

  async function onSubmitSearch({ nameSearch, statusSearch }) {
    setIsFetching(true);
    await pdfTemplateActions.getListPdfTemplate(
      { name: nameSearch || null, status: statusSearch ? parseInt(statusSearch) : 0 },
      0,
      localStorage && localStorage.getItem('currentPageSizeStore')
        ? localStorage.getItem('currentPageSizeStore')
        : 10
    );
    setRefreshPagingDefault(true);
    setIsFetching(false);
  }

  const recallApi = async (fetchDataOptions) => {
    setIsFetching(true);
    await pdfTemplateActions.getListPdfTemplate(
      {
        name: nameSearch || null,
        status: statusSearch ? parseInt(statusSearch) : 1,
        sort_type: fetchDataOptions.sort_type,
        sort_field: fetchDataOptions.sort_field,
      },
      fetchDataOptions.pageIndex,
      fetchDataOptions.pageSize
    );
    setIsFetching(false);
  };

  async function onSubmit({ name, code, data_structure, isActive }) {
    const params = {
      name,
      code,
      data_structure: data_structure ? JSON.parse(data_structure) : null,
      status: isActive ? 1 : 2,
    };
    try {
      let res;
      if (isCreate) {
        res = await pdfTemplateActions.createPdfTemplate(params);
      } else {
        delete params.code;
        res = await pdfTemplateActions.updatePdfTemplate(params, currentTemplateID);
      }

      if (res.status === 200) {
        onClose();
        reset();
        const message = isCreate
          ? 'Create PDF Template success'
          : 'Update PDF Template success';

        toast({
          description: message,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        await pdfTemplateActions.getListPdfTemplate(
          {
            name: nameSearch || null,
            status: statusSearch ? parseInt(statusSearch) : 0,
          },
          0,
          localStorage && localStorage.getItem('currentPageSizeStore')
            ? localStorage.getItem('currentPageSizeStore')
            : 10
        );
      }
    } catch (err) {
      console.warn(err);
      toast({
        description: err?.response?.data?.meta?.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    }
  }

  async function editTemplate(templateID) {
    history.push(`/admin/management/pdf-template-detail/${templateID}`);
  }

  return (
    <Card px='0px'>
      <CardHeader px='22px' mb='12px'>
        <Flex direction='column' display='block'>
          <Text
            float='left'
            color={textColor}
            fontSize='lg'
            fontWeight='bold'
            mb='6px'
          >
            PDF Template Management
          </Text>
          <Stack float='right' direction='row' spacing={4}>
            {checkForbidden(140) && (
              <Button
                size='sm'
                onClick={() => {
                  onOpen();
                  setIsCreate(true);
                  reset();
                }}
                leftIcon={<AddIcon />}
                colorScheme='blue'
                variant='outline'
              >
                Add Template
              </Button>
            )}
          </Stack>
          <Modal
            closeOnOverlayClick={false}
            isOpen={isOpen}
            onClose={() => {
              onClose();
              reset();
            }}
            size='2xl'
          >
            <ModalOverlay />
            <form onSubmit={handleSubmit(onSubmit)}>
              <ModalContent>
                <ModalHeader>
                  {isCreate ? 'Add New PDF Template' : 'Edit PDF Template'}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                  <FormControl isRequired>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Code
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      ms='4px'
                      type='text'
                      placeholder='Code'
                      maxLength={250}
                      mb={errors.code?.message ? '5px' : { lg: '24px', sm: '0' }}
                      size='md'
                      name='code'
                      {...register('code')}
                      isInvalid={errors.code}
                      errorBorderColor='red.300'
                      isDisabled={!isCreate}
                    />
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.code?.message}
                    </Text>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Name
                    </FormLabel>
                    <Input
                      fontSize='sm'
                      ms='4px'
                      type='text'
                      placeholder='Name'
                      maxLength={250}
                      mb={errors.name?.message ? '5px' : { lg: '24px', sm: '0' }}
                      size='md'
                      name='name'
                      {...register('name')}
                      isInvalid={errors.name}
                      errorBorderColor='red.300'
                    />
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.name?.message}
                    </Text>
                  </FormControl>
                  <FormControl mb={errors.data_structure?.message ? '5px' : { lg: '24px', sm: '0' }}>
                    <FormLabel ms='4px' fontSize='sm' fontWeight='normal'>
                      Data Structure (JSON)
                    </FormLabel>
                    <Textarea
                      fontSize='sm'
                      ms='4px'
                      placeholder='{"key": "value"}'
                      size='md'
                      name='data_structure'
                      {...register('data_structure')}
                      isInvalid={errors.data_structure}
                      errorBorderColor='red.300'
                      minH='120px'
                      fontFamily='monospace'
                      style={{ whiteSpace: 'pre', overflowX: 'auto' }}
                    />
                    <Text color='red' fontSize={14} mb='10px' ml='5px'>
                      {errors.data_structure?.message}
                    </Text>
                  </FormControl>
                  <FormControl>
                    <Switch
                      id='isChecked'
                      defaultChecked={isCreate && true}
                      name='isActive'
                      {...register('isActive')}
                      size='sm'
                      ms='4px'
                    >
                      Active
                    </Switch>
                  </FormControl>
                </ModalBody>
                <ModalFooter>
                  <Button
                    colorScheme='blue'
                    mr={3}
                    type='submit'
                    isLoading={isSubmitting}
                  >
                    {isCreate ? 'Create' : 'Update'}
                  </Button>
                  <Button
                    onClick={() => {
                      onClose();
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                </ModalFooter>
              </ModalContent>
            </form>
          </Modal>
        </Flex>
      </CardHeader>
      <CardBody>
        {!checkForbidden(139) ? (
          <Flex justify='center' align='center' height='200px' color='gray.500'>
            You do not have permission to view this page.
          </Flex>
        ) : (
          <>
            <Card>
              <form
                id='pdf-template-search-form'
                onSubmit={handleSubmitSearch(onSubmitSearch)}
              >
                <Flex
                  direction='row'
                  alignItems='center'
                  mb={3}
                  gap={4}
                  flexWrap={'wrap'}
                >
                  <Box direction='row' alignItems='center' display='block'>
                    <FormLabel ms='4px' fontSize='xs' fontWeight='semibold'>
                      Template Name
                    </FormLabel>
                    <Input
                      _placeholder={{ color: 'gray.400' }}
                      variant='main'
                      type='text'
                      placeholder='Name'
                      w='300px'
                      size='sm'
                      fontSize='sm'
                      name='nameSearch'
                      {...registerSearch('nameSearch')}
                      _focus={{ borderColor: 'blue.500' }}
                    />
                  </Box>
                  <Box direction='row' alignItems='center' display='block'>
                    <FormLabel fontWeight='semibold' fontSize='xs' ms='4px'>
                      Status
                    </FormLabel>
                    <Select
                      ms='4px'
                      type='text'
                      defaultValue='1'
                      size='sm'
                      name='statusSearch'
                      {...registerSearch('statusSearch')}
                      width='100px'
                    >
                      {STATUS_OPTIONS.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Select>
                  </Box>
                </Flex>
                <Flex justify='center' align='center' gap='8px' mt={3}>
                  <Box direction='row' alignItems='center' display='block'>
                    <Button
                      leftIcon={<RxReset />}
                      variant='primary'
                      w='88px'
                      h='33px'
                      onClick={() => {
                        resetSearch();
                      }}
                    >
                      Reset
                    </Button>
                  </Box>
                  <Box direction='row' alignItems='center' display='block'>
                    <Button
                      leftIcon={<BsSearch />}
                      variant='primary'
                      w='88px'
                      h='33px'
                      type='submit'
                    >
                      Search
                    </Button>
                  </Box>
                </Flex>
              </form>
            </Card>
            <SearchTable2
              tableData={newListTemplates}
              columnsData={columnsDataPdfTemplate}
              totalRow={totalRows.pdfTemplates}
              edit={editTemplate}
              listPermissions={[141]}
              actionsActive={['edit']}
              loadData={(fetchDataOptions) => recallApi(fetchDataOptions)}
              isFetching={isFetching}
              refreshPagingDefault={refreshPagingDefault}
            />
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default DataTablePdfTemplate;
